import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import AuthBackground from '../components/auth/AuthBackground';
import AuthCard from '../components/auth/AuthCard';
import AuthForm from '../components/auth/AuthForm';
import PreferencesForm from '../components/auth/PreferencesForm';
import { loginUser, registerUser, updateUserPreferences } from '../services/userService';
import './LoginPage.css';

const backgroundImage = {
  src: 'https://lh3.googleusercontent.com/aida-public/AB6AXuCNIOdsPm9X0WlhGsQUpVnHZvTqhh_MpbvngZuTm6dC0YKSi7ZcSTlBKm6QiYH1awkErcvn3C_8xdazToD_RSvI090h3o0BpLfrev5Q9hHUkmmXGVMFn0RQbDNa1ZqEsbjyKrXYGZC1Zu_CZj7EQ_PgWN3bAReBV2_ML1g6XT06ZDrAJHPDp0CZZtlJiEiFwIw6e_WqzveGNTKhEsJwE3zLcZRrmBzk7nmPsufQID1JFCcAfGgXuVdthL5bVecnREIhNThvpWPsg1M',
  alt: 'Chef preparing fresh ingredients on a wooden table',
};

const backgroundCaption = {
  label: 'Verdant Harvest',
  value: 'Artisanal Living',
};

const brand = {
  mark: 'Wellness Craft',
};

const toNameCase = (value = '') => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return '';
  }

  return trimmedValue.charAt(0).toUpperCase() + trimmedValue.slice(1).toLowerCase();
};

const sanitizeLoginValue = (value = '') =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '.')
    .replace(/^[._-]+|[._-]+$/g, '')
    .slice(0, 40);

const buildLoginValue = (candidate = '', fallback = 'member') => {
  const normalized = sanitizeLoginValue(candidate) || sanitizeLoginValue(fallback) || 'member';
  return (normalized.length >= 3 ? normalized : `${normalized}user`).slice(0, 40);
};

const buildRegistrationPayload = ({ firstName, lastName, email, login, password, avatarUrl }) => {
  const normalizedEmail = email.trim().toLowerCase();
  const [localPart = 'member'] = normalizedEmail.split('@');

  return {
    firstName: toNameCase(firstName || 'Wellness'),
    lastName: toNameCase(lastName || 'Member'),
    email: normalizedEmail,
    login: buildLoginValue(login, localPart),
    password,
    ...(avatarUrl ? { avatarUrl } : {}),
    healthPreferences: ['NONE'],
  };
};

const LoginPage = ({ isModal = false, onClose = () => {}, onAuthSuccess = () => {} }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const a = t.auth;
  const p = t.preferences || {};
  const [authMode, setAuthMode] = useState('login');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: 'idle', message: '' });
  const [wizardStep, setWizardStep] = useState('credentials'); // 'credentials' | 'preferences'
  const [registeredUser, setRegisteredUser] = useState(null);

  useEffect(() => {
    if (!isModal) {
      return undefined;
    }

    const originalOverflow = document.body.style.overflow;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isModal, onClose]);

  const handleModeChange = (mode) => {
    setAuthMode(mode);
    setFeedback({ type: 'idle', message: '' });
  };

  const finalizeAuth = (authPayload, message) => {
    setFeedback({ type: 'success', message });
    onAuthSuccess(authPayload);

    if (!isModal) {
      navigate('/explorer');
    }
  };

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    setFeedback({
      type: 'idle',
      message: authMode === 'register' ? a.submittingRegister : a.submittingLogin,
    });

    let authPayload = null;

    try {
      if (authMode === 'register') {
        authPayload = await registerUser(
          buildRegistrationPayload({
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            login: values.login,
            password: values.password,
          })
        );
      } else {
        authPayload = await loginUser({
          email: values.identifier.trim().toLowerCase(),
          password: values.password,
        });
      }
    } catch (error) {
      setFeedback({
        type: 'error',
        message:
          error.message ??
          (authMode === 'register'
            ? 'Unable to create your account right now. Please try again shortly.'
            : 'Unable to sign in right now. Please try again shortly.'),
      });
    } finally {
      setIsSubmitting(false);
    }

    if (authPayload) {
      if (authMode === 'register') {
        setRegisteredUser(authPayload);
        onAuthSuccess(authPayload);
        setWizardStep('preferences');
      } else {
        finalizeAuth(
          authPayload,
          `Welcome, ${authPayload?.data?.login || authPayload?.data?.firstName || 'friend'}!`
        );
      }
    }
  };

  const handlePreferencesSubmit = async (preferencesData) => {
    if (!registeredUser) return;
    setIsSubmitting(true);
    try {
      const userId = registeredUser?.data?._id || registeredUser?.data?.id;
      await updateUserPreferences(userId, preferencesData);
    } catch (_err) {
      // Non-fatal — preferences can be set later from PreferencesPage
    } finally {
      setIsSubmitting(false);
    }
    if (!isModal) {
      navigate('/');
    } else {
      onClose();
    }
  };

  const handlePreferencesSkip = () => {
    if (!isModal) {
      navigate('/');
    } else {
      onClose();
    }
  };

  const handleForgotPassword = () => {
    setFeedback({
      type: 'error',
      message: 'Password recovery is not connected yet. Please use your existing details to sign in.',
    });
  };

  const isRegisterMode = authMode === 'register';

  const preferencesPanel = (
    <>
      <AuthCard brand={brand} title={p.title || 'Your Preferences'} subtitle={p.subtitle || 'Help us personalize your experience'}>
        <p className="pref-wizard-step-label">{(p.stepOf || 'Set up your profile')}</p>
        <PreferencesForm
          onSubmit={handlePreferencesSubmit}
          onSkip={handlePreferencesSkip}
          isSubmitting={isSubmitting}
          labels={p}
        />
      </AuthCard>
      {!isModal ? (
        <div className="auth-mobile-footer">
          <p>© {new Date().getFullYear()} The Digital Curator</p>
        </div>
      ) : null}
    </>
  );

  const authPanel = (
    <>
      <AuthCard
        brand={brand}
        title={isRegisterMode ? a.registerCardTitle : a.loginCardTitle}
        subtitle={isRegisterMode ? a.registerCardSubtitle : a.loginCardSubtitle}
      >
        <AuthForm
          mode={authMode}
          onSubmit={handleSubmit}
          submitLabel={isSubmitting ? (isRegisterMode ? a.submittingRegister : a.submittingLogin) : (isRegisterMode ? a.submitRegister : a.submitLogin)}
          forgotLabel={isRegisterMode ? '' : a.forgotPassword}
          onForgotPassword={handleForgotPassword}
          providers={[]}
          isSubmitting={isSubmitting}
          labels={{
            firstName: a.firstName,
            firstNamePlaceholder: a.firstNamePlaceholder,
            lastName: a.lastName,
            lastNamePlaceholder: a.lastNamePlaceholder,
            username: a.username,
            usernamePlaceholder: a.usernamePlaceholder,
            emailAddress: a.emailAddress,
            emailPlaceholder: a.emailPlaceholder,
            emailOrUsername: a.emailOrUsername,
            emailOrUsernamePlaceholder: a.emailOrUsernamePlaceholder,
            password: a.password,
          }}
        />
        {feedback.message ? <p className={`auth-feedback is-${feedback.type}`}>{feedback.message}</p> : null}
        <footer className="auth-card-footer">
          <p>
            {isRegisterMode ? a.haveAccount : a.noAccount}
            <button type="button" onClick={() => handleModeChange(isRegisterMode ? 'login' : 'register')}>
              {isRegisterMode ? a.switchToLogin : a.switchToRegister}
            </button>
          </p>
        </footer>
      </AuthCard>
      {!isModal ? (
        <div className="auth-mobile-footer">
          <p>© {new Date().getFullYear()} The Digital Curator</p>
        </div>
      ) : null}
    </>
  );

  if (isModal) {
    return (
      <div className="login-modal-backdrop" role="presentation" onClick={onClose}>
        <div
          className="login-modal-shell"
          role="dialog"
          aria-modal="true"
          aria-label="Account sign in"
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            className="login-modal-close"
            aria-label="Close sign-in dialog"
            onClick={onClose}
          >
            <span className="material-symbols-rounded" aria-hidden="true">
              close
            </span>
          </button>
          {wizardStep === 'preferences' ? preferencesPanel : authPanel}
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <AuthBackground image={backgroundImage} caption={backgroundCaption} />
      <main className="login-content">
        {wizardStep === 'preferences' ? preferencesPanel : authPanel}
      </main>
    </div>
  );
};

export default LoginPage;
